import { useState, useEffect, useRef } from 'react';
import { TalkToTeamButton } from './learner/TalkToTeamButton';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { MessageCircle, X, Send, Loader2, AlertTriangle, Phone, ChevronRight, Stethoscope, Salad, Sparkles, ArrowUpRight, ShieldAlert, RotateCcw, Star, ChevronLeft, Play, Info, Video, CalendarDays } from 'lucide-react';
import { VideoPlayerDialog } from './prenatal/VideoPlayerDialog';
import { toast } from 'sonner';

/** V44 — in-app callback request card (replaces the WhatsApp hand-off). */
function CallbackCard({ action }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-white p-3" data-testid="soulai-callback-card">
      <p className="text-sm font-medium text-slate-900">{action.title || 'Request a callback'}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{action.subtitle || 'Our team will call you within 24 hours (except Sunday).'}</p>
      <TalkToTeamButton category={action.category || 'support'} message={action.prefill_text} label={action.cta || 'Request a callback'}
        testId="soulai-callback-btn" className="mt-2" />
    </div>
  );
}
import { ConsultThread } from './medical/ConsultThread';

/** V51 — the OPS team answered inside the chat; the learner replies without leaving SoulAI. */
function ReplyToTeamCard({ action }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post('/api/support/reply', { message: text.trim(), enquiry_id: action.enquiry_id || undefined });
      setSent(r.data.message || 'Sent to our team.');
      setText('');
    } catch { toast.error('Could not send your reply. Please try again.'); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-emerald-200 bg-white p-3" data-testid="soulai-reply-team-card">
      <p className="text-xs font-medium text-slate-900">{action.title || 'Reply to our team'}</p>
      {sent ? <p className="text-xs text-emerald-700 mt-1" data-testid="soulai-reply-team-sent">{sent}</p> : (
        <div className="flex gap-2 mt-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Type your reply…" className="text-xs min-h-[40px]" data-testid="soulai-reply-team-input" />
          <Button size="sm" onClick={send} disabled={busy || !text.trim()} data-testid="soulai-reply-team-btn">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}</Button>
        </div>
      )}
    </div>
  );
}

export default function SoulAIWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [showNudge, setShowNudge] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [consentPrompt, setConsentPrompt] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [consultId, setConsultId] = useState(null);
  const [consultMeta, setConsultMeta] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [practiceVideo, setPracticeVideo] = useState(null);
  const [upcomingCall, setUpcomingCall] = useState(null);
  const scrollRef = useRef(null);
  const nudgeTimer = useRef(null);
  const ratedConsultRef = useRef(null);
  const closedDismissedRef = useRef(new Set());

  // V14.6 Phase 3 — when the widget opens, if a live nutritionist consult is
  // already in progress, drop straight into consult mode. If there's no live
  // consult but one was just closed (unrated, <24h), surface its closed state
  // so the learner sees the Reopen/Rate banner + auto rate dialog in-widget.
  // V66 — also pick up the next scheduled video consultation with the dietician.
  useEffect(() => {
    if (open && !consultId) {
      api.get('/api/medical/my-consults')
        .then(r => {
          setUpcomingCall(r.data.upcoming_call || null);
          if (r.data.active) setConsultId(r.data.active.id);
          else if (r.data.recent_closed && !closedDismissedRef.current.has(r.data.recent_closed.id)) {
            setConsultId(r.data.recent_closed.id);
          }
        })
        .catch(() => {});
    }
  }, [open, consultId]);

  // Auto-surface the rating prompt once when a consult closes (dismissable).
  useEffect(() => {
    if ((consultMeta?.status === 'completed' || consultMeta?.status === 'closed') && consultMeta?.id
        && ratedConsultRef.current !== consultMeta.id && consultMeta?.rating_pending) {
      ratedConsultRef.current = consultMeta.id;
      setShowRating(true);
    }
  }, [consultMeta]);

  // Show nudge after 20 seconds
  useEffect(() => {
    nudgeTimer.current = setTimeout(() => {
      if (!open) setShowNudge(true);
    }, 20000);
    return () => clearTimeout(nudgeTimer.current);
  }, []);

  // V14.2 — Listen for `open-soulai` event so other surfaces (Vault summary
  // dialog, etc.) can pop the chat in-page with a pre-filled question.
  // V16.2 — `expertMode: true` (Speak to Expert) opens with a local intro
  // asking what the learner needs; SoulAI then routes medical ↔ team.
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      setOpen(true);
      setShowNudge(false);
      if (detail.expertMode) {
        setMessages(prev => prev.some(m => m.local_intro) ? prev : [...prev, {
          role: 'assistant',
          local_intro: true,
          content: "I'll help you reach the right person. Tell me what you need — is it a health or medical concern, or something about our classes, membership or services? Type it below and I'll take it from there.",
        }]);
      }
      if (detail.prefill) {
        setInput(detail.prefill);
        if (detail.autoSubmit) {
          // Defer a tick so the dialog has mounted before we submit
          setTimeout(() => {
            const evt = new CustomEvent('soulai-internal-submit');
            window.dispatchEvent(evt);
          }, 200);
        }
      }
    };
    window.addEventListener('open-soulai', handler);
    return () => window.removeEventListener('open-soulai', handler);
  }, []);

  // Load prompts
  useEffect(() => {
    if (open && prompts.length === 0) {
      api.get('/api/soulai/prompts').then(res => setPrompts(res.data.prompts || [])).catch(() => {});
    }
  }, [open]);

  // Load history (once per mount; keeps any local expert-mode intro at the end)
  const historyLoadedRef = useRef(false);
  useEffect(() => {
    if (open && !historyLoadedRef.current) {
      historyLoadedRef.current = true;
      api.get('/api/soulai/history?limit=20').then(res => {
        const hist = res.data.messages?.map(m => ({
          role: m.role,
          content: m.content,
          is_emergency: m.is_emergency,
          route_to: m.route_to,
          actions: m.actions || [],
          from_team: !!m.from_team,
          to_team: !!m.to_team,
          agent_label: m.agent_label,
          agent_name: m.agent_name,
          enquiry_id: m.enquiry_id,
        })) || [];
        api.post('/api/support/messages/read').catch(() => {});
        setMessages(prev => [...hist, ...prev.filter(m => m.local_intro)]);
      }).catch(() => {});
    }
  }, [open]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    
    try {
      const res = await api.post('/api/soulai/chat', { message: msg });
      // V14.6 Phase 3 — a live consult is open: the backend routed this message
      // to the nutritionist and SoulAI stayed silent. Switch the widget into
      // consult mode (the message will appear in the live thread).
      if (res.data.consult_mode) {
        setConsultId(res.data.consult_id);
        setLoading(false);
        return;
      }
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        is_emergency: res.data.is_emergency,
        route_to: res.data.route_to,
        assigned_doctor: res.data.assigned_doctor,
        consent_required: res.data.consent_required,
        consent_given: res.data.consent_given,
        disclaimer: res.data.disclaimer,
        actions: res.data.actions || [],
      }]);
      if (typeof res.data.messages_remaining_today === 'number') {
        setRemaining(res.data.messages_remaining_today);
      }
    } catch (err) {
      if (err?.response?.status === 429 && err.response.data?.detailObject?.code === 'DAILY_LIMIT_REACHED') {
        const d = err.response.data.detailObject;
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: d.message,
          daily_limit: true,
          upgrade_cta: d.upgrade_cta,
        }]);
        setRemaining(0);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Sorry, I had trouble processing that. Please try again.',
        }]);
      }
    }
    setLoading(false);
  };

  const grantConsentAndRelay = async ({ doctor, userText }) => {
    try {
      await api.post('/api/doctor-relay/consent', { doctor_id: doctor.doctor_id, granted: true });
      toast.success(`Consent saved. Sharing with Dr. ${doctor.doctor_name}.`);
      setConsentPrompt(null);
      // Trigger relay packet (short summary)
      await api.post('/api/doctor-relay/packets', {
        chief_concern: userText?.slice(0, 180) || 'Patient has a medical question',
        symptom_summary: userText || 'See SoulAI chat',
        triage_tier: 'L3',
      });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✓ Dr. ${doctor.doctor_name} has been notified. They typically reply within 2 hours, and you'll see their reply here.`,
        is_relay_confirmation: true,
      }]);
    } catch (e) {
      toast.error('Could not share with doctor. Please try again.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // V14.6 Phase 2 — connect the learner to the live nutritionist using the
  // context SoulAI gathered. On success we drop a confirmation turn with an
  // "Open consultation" action so they jump straight into the live chat.
  const connectNutritionist = async (action) => {
    if (connecting) return;
    setConnecting(true);
    try {
      const res = await api.post('/api/medical/connect', {
        context: action.context || '',
        concern: action.concern || 'nutrition',
      });
      const cid = res.data.id || res.data._id;
      toast.success('Connected — the nutritionist has been notified.');
      // Enter live consult mode right inside the widget (founder rule: seamless).
      setConsultMeta(null);
      ratedConsultRef.current = null;
      setConsultId(cid);
    } catch (e) {
      const code = e.response?.data?.detailObject?.code;
      if (code === 'NUTRITIONIST_UNAVAILABLE') toast.info('Our nutritionist is currently unavailable. I can still help in the meantime.');
      else if (code === 'ELITE_ONLY') toast.info('Live nutritionist chat is an Elite benefit.');
      else toast.error(e.response?.data?.detail || 'Could not connect. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  const reopenConsult = async (id) => {
    try {
      await api.post(`/api/medical/consult/${id}/reopen`);
      toast.success('Reopened — the nutritionist has been notified.');
      ratedConsultRef.current = null;
      setConsultMeta(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reopen');
    }
  };

  const goTo = (path) => { setOpen(false); navigate(path || '/learner/nutritionist'); };

  if (!user || user.role !== 'learner') return null;

  return (
    <>
      {/* Nudge bubble */}
      {showNudge && !open && (
        <div className="fixed bottom-20 right-4 z-50 max-w-[250px] animate-in slide-in-from-bottom-2" onClick={() => { setShowNudge(false); setOpen(true); }}>
          <div className="bg-white rounded-2xl shadow-lg border p-3 cursor-pointer hover:shadow-xl transition-shadow duration-200">
            <p className="text-sm">Hi {user.name?.split(' ')[0]}! How are you feeling today? I&apos;m here to help.</p>
            <Button variant="ghost" size="sm" className="mt-1 text-xs text-primary p-0 h-auto" onClick={(e) => { e.stopPropagation(); setShowNudge(false); }}>
              <X className="h-3 w-3 mr-1" />Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Launcher Button */}
      <button
        onClick={() => { setOpen(!open); setShowNudge(false); }}
        className="fixed bottom-4 right-4 z-50 h-14 w-14 rounded-2xl bg-[hsl(174,44%,28%)] text-white shadow-lg hover:shadow-xl transition-shadow duration-200 flex items-center justify-center"
        data-testid="soulai-launcher-button"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-20 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] bg-white/95 backdrop-blur rounded-2xl shadow-2xl border flex flex-col overflow-hidden" data-testid="soulai-panel">
          {consultId ? (
            <ConsultPanel
              consultId={consultId}
              meta={consultMeta}
              onMeta={setConsultMeta}
              onReopen={() => reopenConsult(consultId)}
              onRate={() => setShowRating(true)}
              onBack={() => {
                if ((consultMeta?.status === 'completed' || consultMeta?.status === 'closed') && consultId) closedDismissedRef.current.add(consultId);
                setConsultId(null); setConsultMeta(null);
              }}
            />
          ) : (
          <>
          {/* Header */}
          <div className="p-4 border-b bg-[hsl(174,44%,28%)] text-white rounded-t-2xl">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-xl bg-white/20 flex items-center justify-center">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">SoulAI</h3>
                <p className="text-[10px] text-white/70">Your pregnancy companion</p>
              </div>
            </div>
          </div>

          {/* Safety note */}
          <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-100">
            <p className="text-[10px] text-amber-700">Not a substitute for medical advice. Consult your healthcare provider.</p>
          </div>

          {/* V66 — upcoming video consultation with the dietician */}
          {upcomingCall && (
            <button type="button" onClick={() => setConsultId(upcomingCall.consult_id)}
              className="px-3 py-2 bg-sky-50 border-b border-sky-100 flex items-center gap-2 text-left hover:bg-sky-100 transition-colors" data-testid="soulai-upcoming-call-banner">
              <Video className="h-4 w-4 text-sky-700 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-sky-900 truncate">Upcoming: {upcomingCall.title}</p>
                <p className="text-[10px] text-sky-700">{new Date(upcomingCall.scheduled_at).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })} · {upcomingCall.duration_minutes} min</p>
              </div>
              <a href={upcomingCall.join_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                className="text-[10px] font-semibold text-white bg-sky-600 hover:bg-sky-700 rounded-md px-2 py-1 shrink-0" data-testid="soulai-upcoming-call-join">Join</a>
            </button>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="text-center py-6">
                <MessageCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Hi {user.name?.split(' ')[0]}! Ask me anything about your pregnancy journey.</p>
                {prompts.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {prompts.map((p, i) => (
                      <button key={i} onClick={() => sendMessage(p)}
                        className="block w-full text-left p-2 rounded-xl border text-xs hover:bg-muted/40 transition-colors duration-200"
                        data-testid="soulai-quick-prompt">
                        <ChevronRight className="h-3 w-3 inline mr-1 text-primary" />{p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-[hsl(174,44%,28%)] text-white rounded-br-md'
                    : msg.from_team ? 'bg-emerald-50 border border-emerald-200 rounded-bl-md' : 'bg-muted rounded-bl-md'
                }`}>
                  {msg.from_team && (
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 mb-1" data-testid="soulai-team-reply-label">
                      {msg.agent_label || 'Soul Sanskar team'}{msg.agent_name ? ` · ${msg.agent_name}` : ''}
                    </p>
                  )}
                  {msg.to_team && (
                    <p className="text-[10px] uppercase tracking-wide opacity-75 mb-1" data-testid="soulai-to-team-label">Sent to our team</p>
                  )}
                  {msg.is_emergency && (
                    <div className="flex items-center gap-1 mb-1 text-rose-600 bg-rose-50 rounded-lg px-2 py-1 text-xs">
                      <AlertTriangle className="h-3 w-3" />
                      <span className="font-medium">Urgent</span>
                      <a href="tel:+917890903641" className="ml-auto flex items-center gap-1 text-rose-700">
                        <Phone className="h-3 w-3" />Call Team
                      </a>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  {msg.disclaimer && msg.role === 'assistant' && (
                    <p className="text-[10px] mt-1 opacity-60 italic">Consult your healthcare provider for personalized advice.</p>
                  )}
                  {msg.route_to === 'assigned_doctor' && msg.assigned_doctor && msg.role === 'assistant' && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      {msg.consent_required ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="text-xs w-full"
                          onClick={() => {
                            const lastUser = [...messages].reverse().find(m => m.role === 'user');
                            setConsentPrompt({ doctor: msg.assigned_doctor, userText: lastUser?.content });
                          }}
                          data-testid="share-with-doctor-button"
                        >
                          <Stethoscope className="h-3 w-3 mr-1" />
                          Share with Dr. {msg.assigned_doctor.doctor_name}
                          <ChevronRight className="h-3 w-3 ml-1" />
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="text-xs w-full"
                          onClick={async () => {
                            const lastUser = [...messages].reverse().find(m => m.role === 'user');
                            try {
                              await api.post('/api/doctor-relay/packets', {
                                chief_concern: lastUser?.content?.slice(0, 180) || 'Patient question',
                                symptom_summary: lastUser?.content || 'See chat',
                                triage_tier: 'L3',
                              });
                              toast.success(`Dr. ${msg.assigned_doctor.doctor_name} notified`);
                            } catch {
                              toast.error('Could not send');
                            }
                          }}
                          data-testid="notify-doctor-button"
                        >
                          <Stethoscope className="h-3 w-3 mr-1" />
                          Notify Dr. {msg.assigned_doctor.doctor_name}
                        </Button>
                      )}
                    </div>
                  )}
                  {Array.isArray(msg.actions) && msg.actions.length > 0 && msg.role === 'assistant' && (
                    <div className="mt-2 space-y-2" data-testid="soulai-action-cards">
                      {msg.actions.map((a, ai) => (
                        <SoulAIActionCard
                          key={ai}
                          action={a}
                          connecting={connecting}
                          onConnect={connectNutritionist}
                          onNavigate={goTo}
                          onPlayPractice={setPracticeVideo}
                        />
                      ))}
                    </div>
                  )}
                  {msg.daily_limit && msg.role === 'assistant' && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      <Button variant="secondary" size="sm" className="text-xs w-full" data-testid="soulai-upgrade-gold-cta">
                        {msg.upgrade_cta || 'Upgrade to Elite'}
                        <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl rounded-bl-md px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t">
            <div className="flex gap-2">
              <Textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask SoulAI anything..."
                className="min-h-[40px] max-h-[80px] resize-none text-sm"
                data-testid="soulai-message-input"
              />
              <Button size="icon" onClick={() => sendMessage()} disabled={!input.trim() || loading} className="shrink-0" data-testid="soulai-send-button">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
          </>
          )}
        </div>
      )}
      {/* V64 — practice class SoulAI recommended in chat, played inline */}
      <VideoPlayerDialog
        video={practiceVideo}
        open={!!practiceVideo}
        onClose={() => setPracticeVideo(null)}
      />
      {/* Rating dialog (closeable — learner can dismiss without rating) */}
      <RateDialog
        key={consultMeta?.id || consultId || 'rate'}
        consultId={showRating ? (consultMeta?.id || consultId) : null}
        onClose={() => setShowRating(false)}
      />
      {/* Consent dialog (PDF §A.3 — India DPDP explicit consent) */}
      <Dialog open={!!consentPrompt} onOpenChange={(o) => !o && setConsentPrompt(null)}>
        <DialogContent data-testid="consent-dialog">
          <DialogHeader>
            <DialogTitle>Share with Dr. {consentPrompt?.doctor?.doctor_name}?</DialogTitle>
            <DialogDescription>
              Your recent SoulAI chat and pregnancy context will be shared with
              Dr. {consentPrompt?.doctor?.doctor_name}. You can revoke this
              later in Settings → Privacy.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsentPrompt(null)} data-testid="consent-deny">
              Cancel
            </Button>
            <Button onClick={() => grantConsentAndRelay(consentPrompt)} data-testid="consent-allow">
              Allow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// V61.5 — server-named destinations → web routes (the APP maps the same keys to its screens).
const SCREEN_ROUTES = {
  soulai_plan: '/learner/soulai-coach',
  my_bookings: '/learner/book?tab=my-bookings',
  my_bookings_past: '/learner/book?tab=my-bookings&sub=past',
  book_classes: '/learner/book',
  learn: '/learner/learn',
  practice_player: '/learner/soulai-coach',
  nutritionist: '/learner/nutritionist',
};

// V14.6 Phase 2 — renders SoulAI's server-driven action cards inside the chat
// (nutritionist connect/upsell/continue, emergency + crisis helplines).
function SoulAIActionCard({ action, connecting, onConnect, onNavigate, onPlayPractice }) {
  const t = action?.type;

  // V71 — the urgency chip: what she should do, and by when. One per L2+ reply.
  if (t === 'urgency') {
    const tone = action.tone === 'critical'
      ? 'border-red-300 bg-red-50 text-red-800'
      : action.tone === 'warning'
        ? 'border-amber-300 bg-amber-50 text-amber-800'
        : 'border-slate-200 bg-slate-50 text-slate-700';
    return (
      <div
        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] font-semibold ${tone}`}
        data-testid="soulai-urgency-chip"
      >
        {action.tone === 'critical' ? <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
          : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
        <span>{action.label}</span>
      </div>
    );
  }

  // V66 — "open this screen" card (My SoulAI Yoga Plan, My Bookings, …). Was dropped on web.
  if (t === 'open_screen') {
    return (
      <div className="rounded-xl border border-emerald-200 bg-white p-3" data-testid="soulai-open-screen-card">
        <div className="flex items-start gap-2">
          <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <CalendarDays className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-foreground">{action.title || 'Open'}</p>
            {action.subtitle && <p className="text-[11px] text-muted-foreground">{action.subtitle}</p>}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="mt-2 w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
          onClick={() => onNavigate(action.deeplink || SCREEN_ROUTES[action.screen] || '/learner/dashboard')}
          data-testid="soulai-open-screen-btn"
        >
          {action.cta || 'Open'}<ArrowUpRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    );
  }

  // V64 — a concern-matched practice class, playable straight from the chat.
  if (t === 'practice_class') {
    return (
      <div className="rounded-xl border border-emerald-200 bg-white p-3" data-testid="soulai-practice-card">
        <div className="flex items-start gap-2">
          <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Play className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-foreground" data-testid="soulai-practice-title">{action.title}</p>
            {action.subtitle && <p className="text-[11px] text-muted-foreground">{action.subtitle}</p>}
          </div>
        </div>
        {action.note && (
          <p className="mt-2 flex items-start gap-1 text-[11px] text-amber-700" data-testid="soulai-practice-note">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />{action.note}
          </p>
        )}
        <Button
          size="sm"
          className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
          onClick={() => onPlayPractice?.({
            video_id: action.video_id,
            title: action.title,
            duration_minutes: action.duration_minutes,
            chat_practice: true,
          })}
          data-testid="soulai-practice-play-btn"
        >
          {action.cta || 'Play now'}<Play className="h-3 w-3 ml-1" />
        </Button>
      </div>
    );
  }

  if (t === 'connect_nutritionist') {
    return (
      <div className="rounded-xl border border-emerald-200 bg-white p-3" data-testid="soulai-connect-nutritionist-card">
        <div className="flex items-start gap-2">
          <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Salad className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-foreground">{action.title || 'Connect with our nutritionist'}</p>
            <p className="text-[11px] text-muted-foreground">{action.subtitle || 'Free for Elite • she\u2019ll join shortly'}</p>
          </div>
        </div>
        <Button
          size="sm"
          className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
          disabled={connecting}
          onClick={() => onConnect(action)}
          data-testid="soulai-connect-nutritionist-btn"
        >
          {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>{action.cta || 'Connect — free'}<ChevronRight className="h-3 w-3 ml-1" /></>}
        </Button>
      </div>
    );
  }

  if (t === 'open_consult' || t === 'continue_consult') {
    return (
      <div className="rounded-xl border border-emerald-200 bg-white p-3" data-testid="soulai-open-consult-card">
        {action.title && <p className="text-[13px] font-semibold text-foreground">{action.title}</p>}
        {action.subtitle && <p className="text-[11px] text-muted-foreground mb-1">{action.subtitle}</p>}
        <Button
          size="sm"
          variant="outline"
          className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
          onClick={() => onNavigate(action.deeplink || '/learner/nutritionist')}
          data-testid="soulai-open-consult-btn"
        >
          {action.cta || 'Open consultation'}<ArrowUpRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    );
  }

  // V16.2 — non-medical (service/sales) hand-off: opens WhatsApp with the
  // learner's own question pre-filled so she can edit before sending.
  if (t === 'contact_team_whatsapp' || t === 'request_callback') {
    return <CallbackCard action={action} />;
  }

  if (t === 'reply_to_team') {
    return <ReplyToTeamCard action={action} />;
  }

  if (t === 'nutritionist_upsell') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="soulai-nutritionist-upsell-card">
        <div className="flex items-start gap-2">
          <Sparkles className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-amber-900">{action.title || 'Live nutritionist chat is an Elite benefit'}</p>
            <p className="text-[11px] text-amber-700">{action.subtitle}</p>
          </div>
        </div>
        <Button
          size="sm"
          className="mt-2 w-full bg-amber-600 hover:bg-amber-700 text-white text-xs"
          onClick={() => onNavigate(action.deeplink || '/learner/learn')}
          data-testid="soulai-nutritionist-upsell-btn"
        >
          {action.cta || 'Explore Elite'}<ChevronRight className="h-3 w-3 ml-1" />
        </Button>
      </div>
    );
  }

  if (t === 'emergency' || t === 'mental_health_crisis' || t === 'domestic_abuse') {
    const number = action.helpline_number;
    const label = action.helpline_label || action.helpline_name;
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="soulai-helpline-card">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-rose-600 shrink-0" />
          <p className="text-[12px] font-medium text-rose-800">{label || 'Get help now'}</p>
        </div>
        {number && (
          <a href={`tel:${number}`} data-testid="soulai-helpline-call-btn">
            <Button size="sm" className="mt-2 w-full bg-rose-600 hover:bg-rose-700 text-white text-xs">
              <Phone className="h-3.5 w-3.5 mr-1" />{action.cta || `Call ${number}`}
            </Button>
          </a>
        )}
      </div>
    );
  }

  if (t === 'book_consult') {
    return (
      <Button
        size="sm"
        variant="secondary"
        className="w-full text-xs"
        onClick={() => onNavigate(action.deeplink)}
        data-testid="soulai-book-consult-btn"
      >
        {action.cta || `Book ${action.provider_label || 'consultation'}`}<ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    );
  }

  if (t === 'local_doctor_fallback') {
    return (
      <p className="text-[11px] text-muted-foreground italic" data-testid="soulai-local-doctor-note">
        Tip: reach out to your local {action.provider_label || 'doctor'} for this.
      </p>
    );
  }

  return null;
}

// V14.6 Phase 3 — live nutritionist consultation rendered INSIDE the SoulAI
// widget. Once connected, the learner chats with the nutritionist here (SoulAI
// stays silent); messages stream both ways every ~2s. Full history is always
// shown, even after close. Only the nutritionist can close; the learner can
// reopen + rate.
function ConsultPanel({ consultId, meta, onMeta, onReopen, onRate, onBack }) {
  const status = meta?.status;
  const closed = status === 'completed' || status === 'closed';
  const waiting = status === 'waiting';
  const providerName = meta?.doctor_name || 'our nutritionist';
  return (
    <>
      <div className="p-3 border-b bg-emerald-600 text-white flex items-center gap-2" data-testid="consult-header">
        {closed && (
          <button onClick={onBack} className="h-7 w-7 rounded-lg hover:bg-white/15 flex items-center justify-center shrink-0" data-testid="consult-back-btn"><ChevronLeft className="h-4 w-4" /></button>
        )}
        <div className="h-8 w-8 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><Salad className="h-4 w-4" /></div>
        <div className="min-w-0">
          <h3 className="font-semibold text-sm truncate">{closed ? 'Consultation closed' : providerName}</h3>
          <p className="text-[10px] text-white/85 flex items-center gap-1">
            {waiting && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
            {waiting ? 'Connecting you to the nutritionist…' : closed ? 'You can reopen it anytime' : 'Nutrition consultation • live'}
          </p>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex flex-col" data-testid="consult-live-thread">
        <ConsultThread consultId={consultId} composer={!closed} onMeta={onMeta} />
      </div>
      {closed && (
        <div className="p-3 border-t bg-muted/30 flex items-center gap-2" data-testid="consult-closed-actions">
          <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onReopen} data-testid="consult-reopen-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" />Reopen</Button>
          <Button size="sm" variant="outline" className="flex-1" onClick={onRate} data-testid="consult-rate-btn"><Star className="h-3.5 w-3.5 mr-1" />Rate</Button>
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="consult-back-soulai-btn">SoulAI</Button>
        </div>
      )}
    </>
  );
}

// Dismissable rating prompt (✕ in the corner + Skip — never auto-vanishes).
function RateDialog({ consultId, onClose }) {
  const [score, setScore] = useState(0);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!score) { toast.info('Tap a star to rate, or close to skip.'); return; }
    setBusy(true);
    try {
      await api.post(`/api/medical/consult/${consultId}/rate`, { score_out_of_10: score * 2, review_text: text });
      toast.success('Thanks for your feedback!');
      onClose();
    } catch { toast.error('Could not submit rating'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!consultId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="rate-consult-dialog">
        <DialogHeader>
          <DialogTitle>Rate your consultation</DialogTitle>
          <DialogDescription>How was your chat with the nutritionist? This is optional — close anytime.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center gap-1 py-1" data-testid="rate-stars">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} onClick={() => setScore(n)} data-testid={`rate-star-${n}`}>
              <Star className={`h-7 w-7 transition-colors ${n <= score ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} />
            </button>
          ))}
        </div>
        <Textarea placeholder="Any feedback for her? (optional)" value={text} onChange={e => setText(e.target.value)} className="text-sm" rows={2} />
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="rate-skip-btn">Skip</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rate-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
